// src/modules/hr/pages/EmployeeList.jsx — Phase 14
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Input, Select, Tag, Tooltip, Modal,
  Form, DatePicker, message, Space, Divider, Popconfirm, InputNumber, Tabs, Segmented, Statistic, Switch
} from 'antd';
import { TeamOutlined, ReloadOutlined, PlusOutlined, EditOutlined, EyeOutlined, SearchOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchBranches } from '../../branches/api';
import {
  fetchEmployees, createEmployee, updateEmployee, deleteEmployee,
  fetchEmployeeCodeSettings, updateEmployeeCodeSettings,
} from '../api';
import RoleSelect from '../../../components/UI/RoleSelect';
import {
  EMPLOYMENT_TYPE_OPTIONS, EMPLOYMENT_STATUS_OPTIONS, MARITAL_STATUS_OPTIONS,
  GENDER_OPTIONS, tagColor, tagLabel,
} from '../constants/hrConstants';


const { Title, Text } = Typography;

// Maps a form field's first path segment → the tab it lives on, so validation
// errors can jump the user to the correct tab.
const FIELD_TAB = {
  name: 'basic', employeeCode: 'basic', email: 'basic', password: 'basic', role: 'basic',
  adhar: 'basic', pan: 'basic', phone: 'basic', designation: 'basic', department: 'basic', branch: 'basic',
  employmentType: 'employment', employmentStatus: 'employment', joiningDate: 'employment',
  confirmationDate: 'employment', salaryGrade: 'employment',
  gender: 'personal', dateOfBirth: 'personal', maritalStatus: 'personal', bloodGroup: 'personal',
  pfNumber: 'personal', esiNumber: 'personal', currentAddress: 'personal', permanentAddress: 'personal',
  familyDetails: 'personal',
  emergencyContacts: 'contacts', bankDetails: 'contacts',
  compensation: 'salary',
};
const tabForField = (name) => FIELD_TAB[Array.isArray(name) ? name[0] : name] || 'basic';

// Salary structure is stored MONTHLY. The switch only changes how HR enters/views it.
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const sumVals = (o) => Object.values(o || {}).reduce((a, b) => a + (Number(b) || 0), 0);
// Scale every value in an object (earnings/deductions) by a factor.
const scaleObj = (o, factor) => Object.fromEntries(
  Object.entries(o || {}).map(([k, val]) => [k, val == null || val === '' ? val : round2(Number(val) * factor)]),
);

export default function EmployeeList() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [filters, setFilters] = useState({ search: '', branchId: null, employmentStatus: null, employmentType: null });
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [salaryMode, setSalaryMode] = useState('MONTHLY'); // how the salary tab displays values
  const compWatch = Form.useWatch('compensation', form); // live values for the projection

  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeSettings, setCodeSettings] = useState(null);
  const [codeSaving, setCodeSaving] = useState(false);
  const [codeForm] = Form.useForm();
  const serialNext = Form.useWatch('serial', codeForm);
  const malePrefix = Form.useWatch('male', codeForm);
  const femalePrefix = Form.useWatch('female', codeForm);
  const otherPrefix = Form.useWatch('other', codeForm);

  useEffect(() => {
    fetchBranches({ limit: 1000 }).then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.pageSize };
      if (filters.search) params.search = filters.search;
      if (filters.branchId) params.branchId = filters.branchId;
      if (filters.employmentStatus) params.employmentStatus = filters.employmentStatus;
      if (filters.employmentType) params.employmentType = filters.employmentType;
      const res = await fetchEmployees(params);
      setData(res?.data?.data || []);
      setPagination((p) => ({ ...p, current: res?.data?.pagination?.current || 1, total: res?.data?.pagination?.total || 0 }));
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load employees');
    } finally { setLoading(false); }
  }, [filters, pagination.pageSize]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filters]);

  const openCreate = () => { setEditing(null); form.resetFields(); setActiveTab('basic'); setSalaryMode('MONTHLY'); setModalOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setActiveTab('basic');
    // Show the salary in the mode HR last used; stored values are monthly so
    // convert to annual for display when that mode was chosen.
    const storedMode = row.compensation?.inputMode || 'MONTHLY';
    setSalaryMode(storedMode);
    const factor = storedMode === 'ANNUAL' ? 12 : 1;
    const compensation = row.compensation ? {
      ...row.compensation,
      earnings: scaleObj(row.compensation.earnings, factor),
      deductions: scaleObj(row.compensation.deductions, factor),
      // OT rate is per-hour — never annualised
    } : undefined;
    form.setFieldsValue({
      ...row,
      compensation,
      branch: row.branch?._id || row.branch,
      joiningDate: row.joiningDate ? dayjs(row.joiningDate) : null,
      confirmationDate: row.confirmationDate ? dayjs(row.confirmationDate) : null,
      dateOfBirth: row.dateOfBirth ? dayjs(row.dateOfBirth) : null,
    });
    setModalOpen(true);
  };

  // Toggle Monthly ⇄ Annual: rescale the currently shown earnings/deductions so the
  // real salary is preserved (OT rate/hour stays as-is).
  const changeSalaryMode = (mode) => {
    if (mode === salaryMode) return;
    const factor = mode === 'ANNUAL' ? 12 : 1 / 12;
    const comp = form.getFieldValue('compensation') || {};
    form.setFieldsValue({
      compensation: {
        ...comp,
        earnings: scaleObj(comp.earnings, factor),
        deductions: scaleObj(comp.deductions, factor),
      },
    });
    setSalaryMode(mode);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        ...v,
        joiningDate: v.joiningDate ? v.joiningDate.toISOString() : undefined,
        confirmationDate: v.confirmationDate ? v.confirmationDate.toISOString() : undefined,
        dateOfBirth: v.dateOfBirth ? v.dateOfBirth.toISOString() : undefined,
      };
      // Normalise the salary structure to MONTHLY for storage (÷12 if entered annually),
      // remember the entry mode, and auto-derive annual CTC = monthly gross × 12.
      if (v.compensation) {
        const div = salaryMode === 'ANNUAL' ? 12 : 1;
        const earnings = scaleObj(v.compensation.earnings, 1 / div);
        const deductions = scaleObj(v.compensation.deductions, 1 / div);
        payload.compensation = {
          ...v.compensation,
          earnings,
          deductions,
          inputMode: salaryMode,
          ctc: round2(sumVals(earnings) * 12),
        };
      }
      setSaving(true);
      if (editing) {
        await updateEmployee(editing._id, payload);
        message.success('Employee updated');
      } else {
        await createEmployee(payload);
        message.success('Employee created');
      }
      setModalOpen(false);
      load(pagination.current);
    } catch (err) {
      if (err?.errorFields?.length) {
        const labels = err.errorFields.map((f) => (Array.isArray(f.name) ? f.name.join(' → ') : f.name)).join(', ');
        message.error(`Please fill the required fields: ${labels}`);
        // Jump to the tab holding the first error, then scroll to the field.
        const firstField = err.errorFields[0].name;
        setActiveTab(tabForField(firstField));
        setTimeout(() => form.scrollToField(firstField, { behavior: 'smooth', block: 'center' }), 50);
        return;
      }
      message.error(err?.response?.data?.message || err?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const openCodeSettings = async () => {
    setCodeModalOpen(true);
    try {
      const res = await fetchEmployeeCodeSettings();
      const s = res?.data?.data || {};
      setCodeSettings(s);
      codeForm.setFieldsValue({
        serial: s.serial?.next,
        male: s.prefixes?.male,
        female: s.prefixes?.female,
        other: s.prefixes?.other,
      });
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load code settings');
    }
  };

  const saveCodeSettings = async () => {
    try {
      const v = await codeForm.validateFields();
      setCodeSaving(true);
      await updateEmployeeCodeSettings(v);
      message.success('Employee code settings updated');
      setCodeModalOpen(false);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to update code settings');
    } finally { setCodeSaving(false); }
  };

  const codePreview = (prefix) => {
    if (!prefix || !serialNext) return null;
    const padded = String(serialNext).padStart(codeSettings?.serial?.padding || 5, '0');
    return <Text type="secondary" style={{ fontSize: 12 }}>Next code: <Text code>{String(prefix).toUpperCase()}-{padded}</Text></Text>;
  };

  const handleDelete = async (row) => {
    try {
      await deleteEmployee(row._id);
      message.success('Employee deleted');
      load(pagination.current);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Delete failed');
    }
  };

  const columns = [
    { title: 'Code', dataIndex: 'employeeCode', width: 110, render: (v) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text> },
    { title: 'Name', dataIndex: 'name', render: (v, r) => <a onClick={() => navigate(`/hr/employees/${r._id}`)}>{v}</a> },
    { title: 'Designation', dataIndex: 'designation', render: (v) => v || '—' },
    { title: 'Department', dataIndex: 'department', render: (v) => v || '—' },
    { title: 'Branch', dataIndex: ['branch', 'name'], render: (v, r) => r.branch?.name || '—' },
    { title: 'Type', dataIndex: 'employmentType', width: 110, render: (v) => v ? tagLabel(EMPLOYMENT_TYPE_OPTIONS, v) : '—' },
    { title: 'Status', dataIndex: 'employmentStatus', width: 110, render: (v) => <Tag color={tagColor(EMPLOYMENT_STATUS_OPTIONS, v)}>{tagLabel(EMPLOYMENT_STATUS_OPTIONS, v)}</Tag> },
    {
      title: 'Actions', key: 'a', width: 140, fixed: 'right',
      render: (_, r) => (
        <Space>
          <Tooltip title="Profile"><Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/hr/employees/${r._id}`)} /></Tooltip>
          {can('hrEmployees', 'edit') && <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>}
          {can('hrEmployees', 'delete') && (
            <Popconfirm
              title="Delete employee"
              description={`Delete ${r.name}? They will no longer be able to log in.`}
              okText="Delete" okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(r)}
            >
              <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Live salary projection (values shown are in `salaryMode`; derive both views).
  const dispEarn = sumVals(compWatch?.earnings);
  const dispDed = sumVals(compWatch?.deductions);
  const monthlyGross = salaryMode === 'ANNUAL' ? dispEarn / 12 : dispEarn;
  const monthlyDed = salaryMode === 'ANNUAL' ? dispDed / 12 : dispDed;
  const monthlyNet = monthlyGross - monthlyDed;
  const annualGross = monthlyGross * 12;
  const annualNet = monthlyNet * 12;

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><TeamOutlined style={{ marginRight: 8 }} />Employees</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Employee master (extends the User directory)</Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={() => load(pagination.current)} /></Tooltip>
            {can('hrEmployees', 'edit') && <Tooltip title="Employee code serial settings"><Button icon={<SettingOutlined />} onClick={openCodeSettings}>Code Serials</Button></Tooltip>}
            {can('hrEmployees', 'create') && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Employee</Button>}
          </Space>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]}>
          <Col xs={24} sm={8}><Input allowClear prefix={<SearchOutlined />} placeholder="Search name / code / email / phone" onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} /></Col>
          <Col xs={12} sm={5}><Select allowClear showSearch optionFilterProp="children" style={{ width: '100%' }} placeholder="Branch" value={filters.branchId} onChange={(v) => setFilters((f) => ({ ...f, branchId: v || null }))}>{branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}</Select></Col>
          <Col xs={12} sm={5}><Select allowClear style={{ width: '100%' }} placeholder="Status" options={EMPLOYMENT_STATUS_OPTIONS} value={filters.employmentStatus} onChange={(v) => setFilters((f) => ({ ...f, employmentStatus: v || null }))} /></Col>
          <Col xs={12} sm={5}><Select allowClear style={{ width: '100%' }} placeholder="Type" options={EMPLOYMENT_TYPE_OPTIONS} value={filters.employmentType} onChange={(v) => setFilters((f) => ({ ...f, employmentType: v || null }))} /></Col>
        </Row>
      </Card>

      <Table
        rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 1100 }}
        pagination={{ ...pagination, showSizeChanger: true, showTotal: (t) => `${t} employees`, onChange: (p) => load(p) }}
      />

      <Modal
        title={editing ? `Edit — ${editing.name}` : 'Add Employee'}
        open={modalOpen} onCancel={() => setModalOpen(false)} onOk={submit}
        okButtonProps={{ loading: saving }} width={720} destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'basic', forceRender: true, label: 'Basic Info',
                children: (
                  <Row gutter={12}>
                    <Col span={12}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    <Col span={12}><Form.Item name="employeeCode" label="Employee ID"><Input disabled placeholder="Auto-generated on save" /></Form.Item></Col>
                    <Col span={12}><Form.Item name="email" label="Email" rules={[{ required: !editing, type: 'email' }]}><Input disabled={!!editing} /></Form.Item></Col>
                    <Col span={12}>
                      <Form.Item name="password" label={editing ? "New Password (optional)" : "Password"} rules={[{ required: !editing }]}>
                        <Input.Password placeholder={editing ? "Leave blank to keep current" : ""} />
                      </Form.Item>
                    </Col>
                                        {/* Roles are dynamic — a role is a permission set, so one added here
                        also appears in Permission Sets, the Dashboard Builder and the
                        approval templates. */}
                    <Col span={12}><Form.Item name="role" label="Role" rules={[{ required: true }]}><RoleSelect allowCreate /></Form.Item></Col>
                    <Col span={12}><Form.Item name="phone" label="Phone"><Input /></Form.Item></Col>
                    <Col span={12}><Form.Item name="adhar" label="Aadhaar" rules={[{ required: !editing }]}><Input /></Form.Item></Col>
                    <Col span={12}><Form.Item name="pan" label="PAN" rules={[{ required: !editing }]}><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name="designation" label="Designation"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name="department" label="Department"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name="branch" label="Branch"><Select showSearch optionFilterProp="children" allowClear>{branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}</Select></Form.Item></Col>
                  </Row>
                ),
              },
              {
                key: 'employment', forceRender: true, label: 'Employment',
                children: (
                  <Row gutter={12}>
                    <Col span={8}><Form.Item name="employmentType" label="Employment Type"><Select options={EMPLOYMENT_TYPE_OPTIONS} allowClear /></Form.Item></Col>
                    <Col span={8}><Form.Item name="employmentStatus" label="Status"><Select options={EMPLOYMENT_STATUS_OPTIONS} allowClear /></Form.Item></Col>
                    <Col span={8}><Form.Item name="salaryGrade" label="Salary Grade"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name="joiningDate" label="Joining Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="confirmationDate" label="Confirmation Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="isCsr" label="Customer Representative" valuePropName="checked" tooltip="Include in lead round-robin assignment"><Switch checkedChildren="CSR" unCheckedChildren="No" /></Form.Item></Col>
                  </Row>
                ),
              },
              {
                key: 'personal', forceRender: true, label: 'Personal',
                children: (
                  <Row gutter={12}>
                    <Col span={8}><Form.Item name="gender" label="Gender" rules={[{ required: !editing, message: 'Gender sets the employee code prefix' }]}><Select options={GENDER_OPTIONS} allowClear /></Form.Item></Col>
                    <Col span={8}><Form.Item name="dateOfBirth" label="Date of Birth"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="maritalStatus" label="Marital Status"><Select options={MARITAL_STATUS_OPTIONS} allowClear /></Form.Item></Col>
                    <Col span={8}><Form.Item name="bloodGroup" label="Blood Group"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name="pfNumber" label="PF Number"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name="esiNumber" label="ESI Number"><Input /></Form.Item></Col>

                    <Col span={24}><Divider orientation="left" style={{ margin: '12px 0' }}>Addresses</Divider></Col>
                    <Col span={12}><Form.Item name="currentAddress" label="Current Address"><Input.TextArea rows={2} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="permanentAddress" label="Permanent Address"><Input.TextArea rows={2} /></Form.Item></Col>

                    <Col span={24}><Divider orientation="left" style={{ margin: '12px 0' }}>Family Details</Divider></Col>
                    <Col span={8}><Form.Item name={['familyDetails', 'fatherName']} label="Father Name"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['familyDetails', 'motherName']} label="Mother Name"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['familyDetails', 'spouseName']} label="Spouse Name"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['familyDetails', 'fatherPhone']} label="Father Phone"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['familyDetails', 'motherPhone']} label="Mother Phone"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['familyDetails', 'spousePhone']} label="Spouse Phone"><Input /></Form.Item></Col>
                  </Row>
                ),
              },
              {
                key: 'contacts', forceRender: true, label: 'Contacts & Bank',
                children: (
                  <Row gutter={12}>
                    <Col span={24}><Divider orientation="left" style={{ margin: '4px 0 12px' }}>Emergency Contacts</Divider></Col>
                    <Col span={8}><Form.Item name={['emergencyContacts', 0, 'name']} label="EC 1 Name"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['emergencyContacts', 0, 'relationship']} label="EC 1 Relation"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['emergencyContacts', 0, 'phone']} label="EC 1 Phone"><Input /></Form.Item></Col>

                    <Col span={8}><Form.Item name={['emergencyContacts', 1, 'name']} label="EC 2 Name"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['emergencyContacts', 1, 'relationship']} label="EC 2 Relation"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['emergencyContacts', 1, 'phone']} label="EC 2 Phone"><Input /></Form.Item></Col>

                    <Col span={8}><Form.Item name={['emergencyContacts', 2, 'name']} label="EC 3 Name"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['emergencyContacts', 2, 'relationship']} label="EC 3 Relation"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['emergencyContacts', 2, 'phone']} label="EC 3 Phone"><Input /></Form.Item></Col>

                    <Col span={24}><Divider orientation="left" style={{ margin: '12px 0' }}>Bank Details</Divider></Col>
                    <Col span={8}><Form.Item name={['bankDetails', 'accountHolderName']} label="Account Holder"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['bankDetails', 'accountNumber']} label="Account Number"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['bankDetails', 'bankName']} label="Bank Name"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['bankDetails', 'ifsc']} label="IFSC Code"><Input /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['bankDetails', 'branchName']} label="Branch Name"><Input /></Form.Item></Col>
                  </Row>
                ),
              },
              {
                key: 'salary', forceRender: true, label: 'Salary Structure',
                children: (
                  <Row gutter={12}>
                    <Col span={24}>
                      <Space align="center" wrap style={{ marginBottom: 4 }}>
                        <Text strong>Enter amounts as:</Text>
                        <Segmented
                          value={salaryMode}
                          onChange={changeSalaryMode}
                          options={[{ label: 'Monthly', value: 'MONTHLY' }, { label: 'Annual', value: 'ANNUAL' }]}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Showing <b>{salaryMode === 'ANNUAL' ? 'annual' : 'monthly'}</b> figures (₹). Stored & paid monthly.
                        </Text>
                      </Space>
                    </Col>

                    <Col span={24}>
                      <Card size="small" style={{ marginBottom: 8, background: '#fafafa' }}>
                        <Row gutter={12}>
                          <Col xs={12} sm={6}><Statistic title="Monthly Gross" value={round2(monthlyGross)} prefix="₹" precision={2} valueStyle={{ color: '#389e0d', fontSize: 16 }} /></Col>
                          <Col xs={12} sm={6}><Statistic title="Monthly Take-home" value={round2(monthlyNet)} prefix="₹" precision={2} valueStyle={{ color: '#0a58ca', fontSize: 16 }} /></Col>
                          <Col xs={12} sm={6}><Statistic title="Annual Gross (CTC)" value={round2(annualGross)} prefix="₹" precision={2} valueStyle={{ fontSize: 16 }} /></Col>
                          <Col xs={12} sm={6}><Statistic title="Annual Take-home" value={round2(annualNet)} prefix="₹" precision={2} valueStyle={{ fontSize: 16 }} /></Col>
                        </Row>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          A full-attendance month pays {money(round2(monthlyNet))} net. Payslips prorate this by payable days; overtime is added separately.
                        </Text>
                      </Card>
                    </Col>

                    <Col span={24}><Divider orientation="left" style={{ margin: '8px 0 12px' }}>Earnings ({salaryMode === 'ANNUAL' ? 'annual ₹' : 'monthly ₹'})</Divider></Col>
                    <Col span={8}><Form.Item name={['compensation', 'earnings', 'basic']} label="Basic"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'earnings', 'hra']} label="HRA"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'earnings', 'conveyance']} label="Conveyance"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'earnings', 'medicalAllowance']} label="Medical Allowance"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'earnings', 'specialAllowance']} label="Special Allowance"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'earnings', 'otherAllowances']} label="Other Allowances"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>

                    <Col span={24}><Divider orientation="left" style={{ margin: '12px 0' }}>Deductions ({salaryMode === 'ANNUAL' ? 'annual ₹' : 'monthly ₹'})</Divider></Col>
                    <Col span={8}><Form.Item name={['compensation', 'deductions', 'pf']} label="PF"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'deductions', 'esi']} label="ESI"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'deductions', 'professionalTax']} label="Professional Tax"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'deductions', 'tds']} label="Income Tax (TDS)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={8}><Form.Item name={['compensation', 'deductions', 'otherDeductions']} label="Other Deductions"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>

                    <Col span={24}><Divider orientation="left" style={{ margin: '12px 0' }}>Overtime</Divider></Col>
                    <Col span={8}><Form.Item name={['compensation', 'overtimeRatePerHour']} label="OT Rate / hour"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                  </Row>
                ),
              },
            ]}
          />
        </Form>
      </Modal>

      <Modal
        title={<><SettingOutlined style={{ marginRight: 8 }} />Employee Code Serials</>}
        open={codeModalOpen} onCancel={() => setCodeModalOpen(false)} onOk={saveCodeSettings}
        okText="Save" okButtonProps={{ loading: codeSaving }} width={420} destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          One serial number is shared by all employees — it keeps counting up no matter the prefix.
          The prefix comes from the employee's gender.
        </Text>
        <Form form={codeForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="serial" label="Next serial number"
            extra={codeSettings?.serial?.minNext > 1 && <Text type="secondary" style={{ fontSize: 12 }}>Must be {codeSettings.serial.minNext} or higher (serials below are already used)</Text>}
            rules={[{ required: true, message: 'Enter the next serial number' }, { type: 'integer', min: 1, message: 'Enter a positive whole number' }]}>
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="e.g. 1 → 00001" />
          </Form.Item>
          <Divider orientation="left" style={{ margin: '8px 0' }}>Prefixes</Divider>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="male" label="Male" extra={codePreview(malePrefix)}
                rules={[{ required: true, message: 'Required' }, { pattern: /^[A-Za-z]{1,5}$/, message: '1–5 letters' }]}>
                <Input maxLength={5} style={{ textTransform: 'uppercase' }} placeholder="AM" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="female" label="Female" extra={codePreview(femalePrefix)}
                rules={[{ required: true, message: 'Required' }, { pattern: /^[A-Za-z]{1,5}$/, message: '1–5 letters' }]}>
                <Input maxLength={5} style={{ textTransform: 'uppercase' }} placeholder="AF" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="other" label="Other" extra={codePreview(otherPrefix)}
                rules={[{ required: true, message: 'Required' }, { pattern: /^[A-Za-z]{1,5}$/, message: '1–5 letters' }]}>
                <Input maxLength={5} style={{ textTransform: 'uppercase' }} placeholder="AO" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
